import React, { useRef, useState, useLayoutEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { CompendiumItem } from './CompendiumData';
import { ItemIcon } from '../../common/ItemIcon';
import type { Resource } from '../../../types';

interface CompendiumGridProps {
    items: CompendiumItem[];
    ownedCounts: Record<string, number>;
    onSelect: (item: CompendiumItem) => void;
    isDetailsOpen: boolean;
    scrollElement: HTMLElement | null;
}

const CARD_MIN_WIDTH = 100;
const GAP = 12;

export const CompendiumGrid: React.FC<CompendiumGridProps> = ({ items, ownedCounts, onSelect, scrollElement, isDetailsOpen }) => {
    const parentRef = useRef<HTMLDivElement>(null);
    const [columns, setColumns] = useState(4);

    // Consolidated effect: handles both resize observation and isDetailsOpen changes synchronously
    useLayoutEffect(() => {
        const updateColumns = () => {
            if (parentRef.current) {
                const width = parentRef.current.clientWidth;
                const newCols = Math.max(2, Math.floor((width + GAP) / (CARD_MIN_WIDTH + GAP)));
                setColumns(newCols);
            }
        };

        // Initial synchronous calculation
        updateColumns();

        // Observe for resize changes
        const resizeObserver = new ResizeObserver(updateColumns);
        if (parentRef.current) resizeObserver.observe(parentRef.current);

        return () => resizeObserver.disconnect();
    }, [isDetailsOpen]);

    const rowCount = Math.ceil(items.length / columns);

    const rowVirtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => scrollElement,
        estimateSize: () => 140, // Height of card + gap
        overscan: 3,
    });

    return (
        <div
            ref={parentRef}
            style={{
                width: '100%',
                position: 'relative',
                overflow: 'hidden' // Clip content during resize transition
            }}
        >
            <div
                style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const startIndex = virtualRow.index * columns;
                    const rowItems = items.slice(startIndex, startIndex + columns);

                    return (
                        <div
                            key={virtualRow.index}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '124px', // Card height
                                transform: `translateY(${virtualRow.start}px)`,
                                display: 'flex',
                                gap: `${GAP}px`,
                                padding: '0 8px', // Side padding
                                boxSizing: 'border-box',
                            }}
                        >
                            {rowItems.map((item) => {
                                const owned = ownedCounts[item.id] || 0;
                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => onSelect(item)}
                                        className="compendium-card"
                                        style={{
                                            flex: 1,
                                            height: '100%',
                                            background: 'var(--color-bg-card)', // Fallback
                                            borderRadius: '8px',
                                            padding: '12px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textAlign: 'center',
                                            border: `1px solid ${owned > 0 ? 'var(--color-accent)' : 'var(--color-border)'}`,
                                            transition: 'transform 0.1s, box-shadow 0.1s',
                                            opacity: 1, // Always visible
                                            position: 'relative',
                                            overflow: 'hidden',
                                            minWidth: `${CARD_MIN_WIDTH}px` // Enforce min width to prevent squish
                                        }}
                                        title={item.name}
                                    >
                                        <div style={{ marginBottom: '8px', position: 'relative' }}>
                                            <ItemIcon id={item.id as Resource} size={48} />
                                            {owned > 0 && (
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: -4,
                                                    right: -10,
                                                    background: 'var(--color-bg-panel)',
                                                    border: '1px solid var(--color-border)',
                                                    borderRadius: '12px',
                                                    padding: '2px 6px',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 'bold',
                                                    color: 'var(--color-text-primary)',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.5)'
                                                }}>
                                                    {owned >= 1000 ? (owned / 1000).toFixed(1) + 'k' : owned}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            color: owned > 0 ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                                            lineHeight: 1.2,
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '100%'
                                        }}>
                                            {item.name}
                                        </div>
                                        {/* Category Badge */}
                                        <div style={{
                                            fontSize: '0.65rem',
                                            marginTop: '4px',
                                            opacity: 0.7,
                                            textTransform: 'uppercase',
                                            color: 'var(--color-text-muted)'
                                        }}>
                                            {item.subCategory}
                                        </div>
                                    </div>
                                );
                            })}
                            {/* Fill empty space if row is not full */}
                            {Array.from({ length: columns - rowItems.length }).map((_, i) => (
                                <div key={`empty-${i}`} style={{ flex: 1 }} />
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
